/* BUDDY AVANZA — Checkout (/avanzo/comprar) · UX/UI Upgrade
   Sólo visual. Lógica intacta: age-check backend-authoritativo,
   consent gate backend, dual emails, intent creation, payment link.
*/
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, ArrowLeft, Check, ShieldCheck, Lock,
  Sparkles, User, Users2, Calendar, Mail, Phone, GraduationCap,
  AlertTriangle,
} from "lucide-react";
import BuddyAvanzaLegalFooter from "@/components/BuddyAvanzaLegalFooter";

const API = process.env.REACT_APP_BACKEND_URL;
const ACCESS_LABEL = "2 meses";

const RELATIONSHIPS = [
  { value: "madre", label: "Madre" },
  { value: "padre", label: "Padre" },
  { value: "tutor_legal", label: "Tutor(a) legal" },
  { value: "responsable_autorizado", label: "Responsable autorizado" },
];

export default function AvanzoComprar() {
  const [offer, setOffer] = useState(null);
  const [step, setStep] = useState(1);
  const [checkResult, setCheckResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [intent, setIntent] = useState(null);

  const [student, setStudent] = useState({
    full_name: "", date_of_birth: "", grade: "1-bach", email: "",
  });
  const [adult, setAdult] = useState({
    full_name: "", email: "", phone: "", relationship: "madre",
  });
  const [flags, setFlags] = useState({
    parental_consent_accepted: false,
    terms_accepted: false,
    privacy_accepted: false,
    aup_accepted: false,
    ai_notice_acknowledged: false,
    marketing_opt_in: false,
  });

  useEffect(() => {
    axios.get(`${API}/api/avanzo-b2c/offer-config`)
      .then((r) => setOffer(r.data))
      .catch(() => setOffer(null));
    window.scrollTo(0, 0);
  }, []);

  const goToAgeCheck = async () => {
    setError(null); setBusy(true);
    try {
      if (!student.full_name.trim()) throw new Error("Ingresa el nombre del estudiante");
      if (!student.date_of_birth) throw new Error("Ingresa la fecha de nacimiento");
      if (!student.email.trim()) throw new Error("Ingresa el correo del estudiante");
      const { data } = await axios.post(
        `${API}/api/avanzo-b2c/consent/age-check`,
        { student_date_of_birth: student.date_of_birth },
      );
      setCheckResult(data);
      setStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message
        || "No pudimos validar la edad";
      setError(String(detail));
    } finally { setBusy(false); }
  };

  const submitIntent = async () => {
    setError(null); setBusy(true);
    try {
      const isMinor = checkResult?.requirement === "PARENTAL_CONSENT_REQUIRED";
      const payload = {
        student_full_name: student.full_name.trim(),
        student_date_of_birth: student.date_of_birth,
        student_grade: student.grade,
        student_email: student.email.trim().toLowerCase(),
        terms_accepted: !!flags.terms_accepted,
        privacy_accepted: !!flags.privacy_accepted,
        aup_accepted: !!flags.aup_accepted,
        ai_notice_acknowledged: !!flags.ai_notice_acknowledged,
        marketing_opt_in: !!flags.marketing_opt_in,
        source: "public_home",
      };
      if (isMinor) {
        payload.responsible_adult_full_name = adult.full_name.trim();
        payload.responsible_adult_email = adult.email.trim().toLowerCase();
        payload.responsible_adult_phone = adult.phone.trim();
        payload.relationship_to_student = adult.relationship;
        payload.parental_consent_accepted = !!flags.parental_consent_accepted;
      }
      const { data } = await axios.post(
        `${API}/api/avanzo-b2c/checkout/intent`, payload,
      );
      setIntent(data);
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const detail = e?.response?.data?.detail || "No pudimos generar el checkout";
      setError(String(detail));
    } finally { setBusy(false); }
  };

  const goToPayment = () => {
    if (intent?.payment_url) {
      window.location.href = intent.payment_url;
    } else {
      setError("payment_link_not_configured");
    }
  };

  if (!offer) {
    return (
      <div data-testid="avz-comprar-loading" className="min-h-screen bg-white text-slate-600 flex items-center justify-center">
        Cargando…
      </div>
    );
  }

  const isMinor = checkResult?.requirement === "PARENTAL_CONSENT_REQUIRED";
  const price = Number(offer.offer_price).toFixed(2);

  return (
    <div data-testid="avz-comprar" className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 text-slate-900">
      <TopBar price={price} accessDays={offer.access_days} />

      <div className="max-w-6xl mx-auto px-4 pt-8 pb-16">
        <StepIndicator step={step} />

        <div className="mt-8 grid lg:grid-cols-12 gap-8">
          <main className="lg:col-span-8">
            {step === 1 && (
              <Step1 student={student} setStudent={setStudent}
                     busy={busy} onNext={goToAgeCheck} error={error} />
            )}
            {step === 2 && (
              isMinor ? (
                <Step2Minor adult={adult} setAdult={setAdult}
                             flags={flags} setFlags={setFlags}
                             student={student}
                             age={checkResult?.age}
                             onBack={() => setStep(1)}
                             onNext={submitIntent}
                             busy={busy} error={error} />
              ) : (
                <Step2Adult flags={flags} setFlags={setFlags}
                              age={checkResult?.age}
                              onBack={() => setStep(1)}
                              onNext={submitIntent}
                              busy={busy} error={error} />
              )
            )}
            {step === 3 && (
              <Step3 intent={intent} offer={offer} student={student} adult={adult}
                     isMinor={isMinor} onPay={goToPayment}
                     onBack={() => setStep(2)} error={error} />
            )}
          </main>

          <aside className="lg:col-span-4">
            <PriceSummary offer={offer} student={student} isMinor={isMinor} step={step} />
          </aside>
        </div>
      </div>

      <BuddyAvanzaLegalFooter />

      <style>{`
        @keyframes fadeUp { from { opacity:0; transform: translateY(14px);} to { opacity:1; transform: none;} }
        .fade-up { animation: fadeUp .5s cubic-bezier(.2,.7,.3,1) both; }
      `}</style>
    </div>
  );
}


/* ═══════════════════════ TOP BAR ═══════════════════════ */
function TopBar({ price, accessDays }) {
  return (
    <div className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/buddy-avanza" className="flex items-center text-slate-700 hover:text-slate-900 transition-colors"
               data-testid="checkout-back-landing">
          <img src="/buddy-avanza-icon.png" alt="BUDDY AVANZA" className="h-14 w-14 object-contain transition-transform hover:scale-105" />
        </Link>
        <div className="hidden sm:flex items-center gap-3 text-xs text-slate-600">
          <ShieldCheck className="w-4 h-4 text-emerald-500" />
          Pago seguro procesado por N1CO
        </div>
        <div className="text-xs text-slate-700">
          <b className="text-slate-900">US${price}</b> · {ACCESS_LABEL}
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════ STEP INDICATOR ═══════════════════════ */
function StepIndicator({ step }) {
  const items = [
    { n: 1, label: "Estudiante" },
    { n: 2, label: "Consentimiento" },
    { n: 3, label: "Pago" },
  ];
  return (
    <div className="fade-up flex items-center gap-3 sm:gap-6"
          data-testid="checkout-step-indicator">
      {items.map((it, i) => {
        const active = step === it.n;
        const done   = step > it.n;
        return (
          <React.Fragment key={it.n}>
            <div className="flex items-center gap-2"
                  data-testid={`checkout-step-${it.n}${active ? "-active" : done ? "-done" : ""}`}>
              <div className={
                "w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all " +
                (done ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/30"
                      : active ? "bg-gradient-to-br from-[#419cca] to-[#8b5cf6] text-white shadow-lg shadow-[#8b5cf6]/30"
                              : "bg-white text-slate-500 border border-slate-200")
              }>
                {done ? <Check className="w-4 h-4" /> : it.n}
              </div>
              <div className={"hidden sm:block text-sm " + (active ? "text-slate-900 font-semibold" : "text-slate-500")}>
                {it.label}
              </div>
            </div>
            {i < items.length - 1 && (
              <div className={"flex-1 h-px transition-colors " + (done ? "bg-emerald-400" : "bg-slate-200")} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}


/* ═══════════════════════ PRICE SUMMARY (aside) ═══════════════════════ */
function PriceSummary({ offer, student, isMinor, step }) {
  const price = Number(offer.offer_price).toFixed(2);
  const dailyDisplay = offer.daily_equivalent_display;
  return (
    <div className="lg:sticky lg:top-24 fade-up" data-testid="checkout-summary">
      <div className="relative rounded-2xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-900/[0.05] ring-1 ring-slate-900/[0.02] overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#419cca] via-[#8b5cf6] to-[#10b981]" />
        <div className="absolute -top-10 right-0 w-40 h-40 rounded-full blur-3xl opacity-10" style={{ background: "#8b5cf6" }} />
        <div className="relative">
          <div className="flex items-center gap-3">
            <img src="/buddy-avanza-logo-transparent.png" alt="BUDDY AVANZA" className="h-12 w-12 object-contain" />
            <div>
              <div className="text-[10px] tracking-[3px] uppercase text-[#8b5cf6] font-bold">
                BUDDY AVANZA
              </div>
              <div className="text-xs text-slate-600 mt-0.5">{offer.brand_descriptor}</div>
            </div>
          </div>

          <div className="mt-4 flex items-baseline gap-2">
            <span className="text-4xl font-black text-slate-900" data-testid="summary-total">US${price}</span>
            <span className="text-xs text-slate-500">USD</span>
          </div>
          {dailyDisplay && (
            <div className="mt-1 text-xs text-emerald-600 font-semibold" data-testid="summary-daily">
              {dailyDisplay}
            </div>
          )}

          <ul className="mt-5 space-y-2 text-sm text-slate-700">
            {[
              `${ACCESS_LABEL} de acceso`,
              "Pago único · sin renovación automática",
              "Buddy Tutor incluido",
              "Simulaciones AVANZO y refuerzos",
            ].map((f) => (
              <li key={f} className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                {f}
              </li>
            ))}
          </ul>

          {student.full_name && (
            <div className="mt-5 rounded-xl bg-slate-50 border border-slate-100 p-3 text-xs">
              <div className="text-slate-500 uppercase tracking-wider text-[10px]">Estudiante</div>
              <div className="text-slate-800 font-semibold mt-0.5" data-testid="summary-student">
                {student.full_name}
              </div>
              {isMinor && (
                <div className="mt-1 text-slate-500">Con autorización parental</div>
              )}
            </div>
          )}

          <div className="mt-5 flex items-center gap-2 text-[11px] text-slate-500">
            <Lock className="w-3.5 h-3.5" />
            Pago seguro procesado por N1CO. No almacenamos datos de tarjeta.
          </div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center text-[10px] text-slate-500">
        <div className="rounded-lg border border-slate-200 bg-white py-2">Pago único</div>
        <div className="rounded-lg border border-slate-200 bg-white py-2">{ACCESS_LABEL}</div>
        <div className="rounded-lg border border-slate-200 bg-white py-2">Sin mensualidades</div>
      </div>
    </div>
  );
}


/* ═══════════════════════ STEP 1 ═══════════════════════ */
function Step1({ student, setStudent, busy, onNext, error }) {
  const canNext = student.full_name && student.date_of_birth
    && student.grade && student.email;
  return (
    <section data-testid="avz-comprar-step1" className="fade-up rounded-2xl border border-slate-200 bg-white p-8 shadow-lg shadow-slate-900/[0.04]">
      <SectionHeader
        eyebrow="Paso 1 de 3"
        title="¿Quién utilizará BUDDY AVANZA?"
        subtitle="Necesitamos algunos datos del estudiante para preparar su acceso." />

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <FieldDark label="Nombre del estudiante" icon={User}
                    value={student.full_name}
                    onChange={(v) => setStudent({ ...student, full_name: v })}
                    testid="input-student-name" />
        <FieldDark label="Fecha de nacimiento" icon={Calendar} type="date"
                    value={student.date_of_birth}
                    onChange={(v) => setStudent({ ...student, date_of_birth: v })}
                    testid="input-student-dob" />
        <SelectDark label="Grado" icon={GraduationCap}
                     value={student.grade}
                     onChange={(v) => setStudent({ ...student, grade: v })}
                     options={[
                       { value: "1-bach", label: "1° de Bachillerato" },
                       { value: "2-bach", label: "2° de Bachillerato" },
                     ]}
                     testid="input-student-grade" />
        <FieldDark label="Correo electrónico" icon={Mail} type="email"
                    value={student.email}
                    onChange={(v) => setStudent({ ...student, email: v })}
                    testid="input-student-email"
                    hint="Este será su usuario. No pediremos otro." />
      </div>

      {error && <ErrorBanner text={error} />}

      <div className="mt-8">
        <PrimaryButton testid="btn-continue-to-consent"
                       enabled={!!canNext && !busy}
                       label={busy ? "Validando…" : "Continuar"}
                       onClick={onNext} />
      </div>
    </section>
  );
}


/* ═══════════════════════ STEP 2 · MENOR ═══════════════════════ */
function Step2Minor({ adult, setAdult, flags, setFlags, student, age,
                       onBack, onNext, busy, error }) {
  const canNext = adult.full_name && adult.email && adult.phone
    && adult.relationship && flags.parental_consent_accepted
    && flags.terms_accepted && flags.privacy_accepted && flags.aup_accepted;
  const distinctEmails = adult.email.trim().toLowerCase()
    !== student.email.trim().toLowerCase();

  return (
    <section data-testid="avz-comprar-step2-minor" className="fade-up rounded-2xl border border-slate-200 bg-white p-8 shadow-lg shadow-slate-900/[0.04]">
      <SectionHeader
        eyebrow="Paso 2 de 3"
        title="Autorización del adulto responsable"
        subtitle={`Como el estudiante es menor de 18 años (${age} años), necesitamos la autorización de un padre, madre, tutor o responsable autorizado antes de continuar.`}
        badge={<Users2 className="w-4 h-4 text-cyan-300" />}
      />

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <FieldDark label="Nombre del adulto responsable" icon={User}
                    value={adult.full_name}
                    onChange={(v) => setAdult({ ...adult, full_name: v })}
                    testid="input-adult-name" />
        <FieldDark label="Correo del adulto responsable" icon={Mail} type="email"
                    value={adult.email}
                    onChange={(v) => setAdult({ ...adult, email: v })}
                    testid="input-adult-email"
                    hint="Debe ser DIFERENTE al correo del estudiante." />
        <FieldDark label="Teléfono" icon={Phone}
                    value={adult.phone}
                    onChange={(v) => setAdult({ ...adult, phone: v })}
                    testid="input-adult-phone" />
        <SelectDark label="Relación con el estudiante" icon={Users2}
                     value={adult.relationship}
                     onChange={(v) => setAdult({ ...adult, relationship: v })}
                     options={RELATIONSHIPS.map(r => ({ value: r.value, label: r.label }))}
                     testid="input-relationship" />
      </div>

      {!distinctEmails && adult.email && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 text-amber-800 p-3 text-sm"
              data-testid="distinct-emails-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Necesitamos un correo para el estudiante y otro <b>diferente</b> para
            el adulto responsable. Cada uno tendrá su propio acceso.
          </span>
        </div>
      )}

      <Divider label="Consentimientos" />

      <ConsentBox testid="input-parental-consent" required
        checked={flags.parental_consent_accepted}
        onChange={(v) => setFlags({ ...flags, parental_consent_accepted: v })}
        title="Autorización parental"
        body="Confirmo que soy el padre, madre, tutor legal o responsable autorizado del estudiante y autorizo su uso de BUDDY AVANZA." />
      <ConsentBox testid="input-terms" required
        checked={flags.terms_accepted}
        onChange={(v) => setFlags({ ...flags, terms_accepted: v })}
        title="Términos y Condiciones"
        body="He leído y acepto los términos del servicio."
        linkHref="/buddy-avanza/terminos"
        linkLabel="Leer Términos y Condiciones →" />
      <ConsentBox testid="input-privacy" required
        checked={flags.privacy_accepted}
        onChange={(v) => setFlags({ ...flags, privacy_accepted: v })}
        title="Política de Privacidad"
        body="He leído la Política de Privacidad y acepto el tratamiento de datos personales para prestar el servicio."
        linkHref="/buddy-avanza/privacidad"
        linkLabel="Leer Política de Privacidad →" />
      <ConsentBox testid="input-aup" required
        checked={flags.aup_accepted}
        onChange={(v) => setFlags({ ...flags, aup_accepted: v })}
        title="Uso Responsable e Inteligencia Artificial"
        body="He leído la Política de Uso Responsable y el Aviso sobre Inteligencia Artificial. Comprendo que BUDDY AVANZA acompaña el aprendizaje del estudiante y no sustituye su esfuerzo."
        linkHref="/buddy-avanza/uso-responsable"
        linkLabel="Leer Uso Responsable · IA →" />
      <ConsentBox testid="input-marketing" optional
        checked={flags.marketing_opt_in}
        onChange={(v) => setFlags({ ...flags, marketing_opt_in: v })}
        title="Recibir información (opcional)"
        body="Deseo recibir información y novedades sobre BUDDY AVANZA." />

      {error && <ErrorBanner text={error} />}

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <SecondaryButton onClick={onBack} testid="btn-back" label="Atrás" />
        <PrimaryButton testid="btn-continue-to-summary"
                       enabled={!!canNext && distinctEmails && !busy}
                       label={busy ? "Procesando…" : "Continuar al resumen"}
                       onClick={onNext} />
      </div>
    </section>
  );
}


/* ═══════════════════════ STEP 2 · ADULTO ═══════════════════════ */
function Step2Adult({ flags, setFlags, age, onBack, onNext, busy, error }) {
  const canNext = flags.terms_accepted && flags.privacy_accepted && flags.aup_accepted;
  return (
    <section data-testid="avz-comprar-step2-adult" className="fade-up rounded-2xl border border-slate-200 bg-white p-8 shadow-lg shadow-slate-900/[0.04]">
      <SectionHeader
        eyebrow="Paso 2 de 3"
        title="Puedes continuar por tu cuenta"
        subtitle={`Como eres mayor de 18 años (${age} años), puedes aceptar directamente los términos y continuar con tu compra.`}
        badge={<User className="w-4 h-4 text-cyan-300" />}
      />

      <div className="mt-6">
        <ConsentBox testid="input-terms" required
          checked={flags.terms_accepted}
          onChange={(v) => setFlags({ ...flags, terms_accepted: v })}
          title="Términos y Condiciones"
          body="He leído y acepto los términos del servicio."
          linkHref="/buddy-avanza/terminos"
          linkLabel="Leer Términos y Condiciones →" />
        <ConsentBox testid="input-privacy" required
          checked={flags.privacy_accepted}
          onChange={(v) => setFlags({ ...flags, privacy_accepted: v })}
          title="Política de Privacidad"
          body="He leído la Política de Privacidad y acepto el tratamiento de mis datos personales para prestar el servicio."
          linkHref="/buddy-avanza/privacidad"
          linkLabel="Leer Política de Privacidad →" />
        <ConsentBox testid="input-aup" required
          checked={flags.aup_accepted}
          onChange={(v) => setFlags({ ...flags, aup_accepted: v })}
          title="Uso Responsable e Inteligencia Artificial"
          body="He leído la Política de Uso Responsable y el Aviso sobre Inteligencia Artificial. Comprendo que BUDDY AVANZA acompaña el aprendizaje y no sustituye mi esfuerzo."
          linkHref="/buddy-avanza/uso-responsable"
          linkLabel="Leer Uso Responsable · IA →" />
        <ConsentBox testid="input-marketing" optional
          checked={flags.marketing_opt_in}
          onChange={(v) => setFlags({ ...flags, marketing_opt_in: v })}
          title="Recibir información (opcional)"
          body="Deseo recibir información y novedades sobre BUDDY AVANZA." />
      </div>

      {error && <ErrorBanner text={error} />}

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <SecondaryButton onClick={onBack} testid="btn-back" label="Atrás" />
        <PrimaryButton testid="btn-continue-to-summary"
                       enabled={!!canNext && !busy}
                       label={busy ? "Procesando…" : "Continuar al resumen"}
                       onClick={onNext} />
      </div>
    </section>
  );
}


/* ═══════════════════════ STEP 3 — RESUMEN + PAGO ═══════════════════════ */
function Step3({ intent, offer, student, adult, isMinor, onPay, onBack, error }) {
  const ready = intent?.ready_for_payment && intent?.payment_url;
  const price = Number(offer.offer_price).toFixed(2);
  return (
    <section data-testid="avz-comprar-step3" className="fade-up rounded-2xl border border-slate-200 bg-white p-8 shadow-lg shadow-slate-900/[0.04]">
      <SectionHeader
        eyebrow="Paso 3 de 3"
        title="Confirma y paga"
        subtitle="Serás redirigido a N1CO para completar el pago de forma segura."
        badge={<Sparkles className="w-4 h-4 text-fuchsia-300" />}
      />

      <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/60 p-6">
        <div className="flex items-center justify-between text-sm">
          <div>
            <div className="text-[10px] tracking-[3px] uppercase text-[#8b5cf6] font-bold">BUDDY AVANZA</div>
            <div className="text-slate-700 mt-0.5" data-testid="summary-descriptor">{offer.brand_descriptor}</div>
          </div>
          <div className="text-emerald-700 text-xs font-semibold inline-flex items-center gap-1"
                data-testid="summary-consent-ok">
            <Check className="w-3.5 h-3.5" /> Consentimiento completo
          </div>
        </div>
        <div className="mt-4 border-t border-slate-200 pt-4 space-y-1.5 text-sm">
          <Row label="Estudiante"      value={student.full_name}   testid="summary-student" />
          <Row label="Acceso"          value={ACCESS_LABEL} testid="summary-access-days" />
          {isMinor && <Row label="Adulto responsable" value={adult.full_name} testid="summary-adult" />}
          <Row label="Pago único"      value={`US$${price}`} testid="summary-offer-price" />
        </div>
        <div className="mt-4 border-t border-slate-200 pt-4 flex items-baseline justify-between">
          <div className="text-sm text-slate-500">TOTAL</div>
          <div className="text-3xl font-black text-slate-900" data-testid="summary-total-line">US${price}</div>
        </div>
        <div className="mt-2 text-[11px] text-slate-500">
          PAGO ÚNICO · {ACCESS_LABEL} de acceso · Sin mensualidades · Sin renovación automática
        </div>
      </div>

      {!ready && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 text-amber-800 p-3 text-sm"
              data-testid="payment-not-ready-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>La pasarela de pago se está configurando. Vuelve a intentar en unos minutos o contacta a soporte.</span>
        </div>
      )}
      {error && <ErrorBanner text={error} />}

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <SecondaryButton onClick={onBack} testid="btn-back" label="Atrás"
                          icon={ArrowLeft} />
        <PrimaryButton testid="btn-pay"
                       enabled={!!ready}
                       label={`Pagar US$${price}`}
                       onClick={onPay}
                       icon={Lock} />
      </div>
      <p className="mt-3 text-[11px] text-slate-500 leading-relaxed"
         data-testid="cardholder-disclosure">
        La transacción debe ser realizada por el titular del medio de pago
        o por una persona debidamente autorizada para utilizarlo. Consulta
        nuestra <a href="/buddy-avanza/terminos#reembolsos" target="_blank" rel="noopener noreferrer"
                    data-testid="cardholder-refund-link"
                    className="underline hover:text-slate-700">
          política de reembolsos, retracto y reversión de pagos
        </a>.
      </p>
      <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-500">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
        Pago seguro procesado por N1CO. Nunca recibimos ni almacenamos datos de tu tarjeta.
      </div>
    </section>
  );
}

function Row({ label, value, strike, accent, testid }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-slate-500">{label}</span>
      <span className={
        (strike ? "line-through text-slate-400 " : "text-slate-800 ") +
        (accent ? "!text-emerald-600 font-semibold " : "font-medium ")
      } data-testid={testid}>{value}</span>
    </div>
  );
}


/* ═══════════════════════ SHARED COMPONENTS ═══════════════════════ */
function SectionHeader({ eyebrow, title, subtitle, badge }) {
  return (
    <div>
      <div className="text-[10px] tracking-[3px] uppercase text-[#419cca] font-bold flex items-center gap-2">
        {badge}{eyebrow}
      </div>
      <h1 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
      {subtitle && <p className="mt-2 text-sm text-slate-600 leading-relaxed">{subtitle}</p>}
    </div>
  );
}

function FieldDark({ label, value, onChange, type = "text", testid, hint, icon: Icon }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-600 mb-1.5 flex items-center gap-2 font-semibold">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </span>
      <input type={type} value={value}
              onChange={(e) => onChange(e.target.value)}
              data-testid={testid}
              className="w-full px-4 py-2.5 text-sm rounded-xl bg-white border border-slate-200 focus:border-[#419cca] focus:ring-2 focus:ring-[#419cca]/15 outline-none transition-all placeholder:text-slate-400 text-slate-900"
              autoComplete="off" />
      {hint && <span className="mt-1 text-[11px] text-slate-500 block">{hint}</span>}
    </label>
  );
}

function SelectDark({ label, value, onChange, options, testid, icon: Icon }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-600 mb-1.5 flex items-center gap-2 font-semibold">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </span>
      <select value={value}
               onChange={(e) => onChange(e.target.value)}
               data-testid={testid}
               className="w-full px-4 py-2.5 text-sm rounded-xl bg-white border border-slate-200 focus:border-[#419cca] focus:ring-2 focus:ring-[#419cca]/15 outline-none transition-all text-slate-900">
        {options.map((o) => (
          <option key={o.value} value={o.value} className="bg-white text-slate-900">
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function ConsentBox({ checked, onChange, title, body, required, optional, testid, linkHref, linkLabel }) {
  return (
    <label className={
      "block cursor-pointer rounded-xl border p-4 mb-3 transition-all " +
      (checked ? "border-[#419cca]/60 bg-[#419cca]/[0.04]"
                : "border-slate-200 bg-white hover:bg-slate-50")
    }>
      <div className="flex items-start gap-3">
        <input type="checkbox" checked={!!checked}
                onChange={(e) => onChange(e.target.checked)}
                data-testid={testid}
                className="mt-1 w-4 h-4 accent-[#419cca]" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-900">
            {title}{required && <span className="text-rose-500 ml-1">*</span>}
            {optional && <span className="text-slate-400 text-xs ml-2">Opcional</span>}
          </div>
          <div className="text-xs text-slate-600 mt-1">{body}</div>
          {linkHref && (
            <a
              href={linkHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid={`${testid}-link`}
              className="inline-block mt-2 text-xs font-semibold text-[#419cca] hover:text-[#2f7dab] underline underline-offset-2"
            >
              {linkLabel || "Leer documento →"}
            </a>
          )}
        </div>
      </div>
    </label>
  );
}

function Divider({ label }) {
  return (
    <div className="flex items-center gap-3 my-7">
      <div className="flex-1 h-px bg-slate-200" />
      <div className="text-[10px] tracking-[3px] uppercase text-slate-500 font-semibold">{label}</div>
      <div className="flex-1 h-px bg-slate-200" />
    </div>
  );
}

function PrimaryButton({ enabled, label, onClick, testid, icon: Icon }) {
  return (
    <button onClick={onClick} disabled={!enabled} data-testid={testid}
             className={
               "group flex-1 inline-flex items-center justify-center px-6 py-4 rounded-full text-base font-semibold transition-all " +
               (enabled
                 ? "text-white bg-gradient-to-r from-[#419cca] to-[#8b5cf6] hover:brightness-110 shadow-lg shadow-[#8b5cf6]/25 hover:scale-[1.02] active:scale-95"
                 : "text-slate-400 bg-slate-100 border border-slate-200 cursor-not-allowed")
             }>
      {Icon && <Icon className="w-4 h-4 mr-2" />}
      {label}
      {enabled && !Icon && <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />}
    </button>
  );
}

function SecondaryButton({ onClick, testid, label, icon: Icon }) {
  return (
    <button onClick={onClick} data-testid={testid}
             className="inline-flex items-center justify-center px-6 py-4 rounded-full text-sm font-semibold text-slate-800 bg-white border border-slate-200 hover:bg-slate-50 transition-all">
      {Icon && <Icon className="w-4 h-4 mr-2" />}
      {label}
    </button>
  );
}

function ErrorBanner({ text }) {
  return (
    <div className="mt-4 flex items-start gap-3 rounded-xl border border-rose-300 bg-rose-50 text-rose-700 p-3 text-sm"
          data-testid="checkout-error">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{String(text)}</span>
    </div>
  );
}
